/**
 * Staff dashboard — pure bucketing logic.
 *
 * Splits a staff member's quotes and orders into the dashboard's work zones
 * and computes outstanding balances. No I/O; unit-tested. The page layer is
 * responsible for fetching and for narrowing to the current user.
 */
import type { QuoteListLike } from '../services/quoteService';
import type { Order, OrderStatus } from '../types';
import { sumCreditablePayments } from '../../shared/depositGate';
import { outstandingCents } from '../../shared/money';
import { creditableLedgerEntries } from '../../shared/creditableLedger';
import { effectiveQuoteTotal } from './quoteEffectiveTotal';

/** Pre-sale quote statuses where the rep still has work to do on the quote
 *  itself. CLIENT_CONFIRMED graduates off the quote views onto the order
 *  surface (it surfaces via `activeOrders`); ON_HOLD is still the rep's to
 *  re-engage (quote/order separation, Slice 4). */
const ONGOING_QUOTE_STATUSES = new Set<string>(['DRAFT', 'SENT', 'ON_HOLD']);

/** Order statuses that mean "confirmed job in progress". */
const ACTIVE_ORDER_STATUSES = new Set<OrderStatus>([
  'CONFIRMED',
  'MEASURING',
  'IN_PRODUCTION',
  'SHIPPED',
  'RECEIVED',
  'READY_FOR_DELIVERY',
  'ON_HOLD',
]);

/** Order statuses that mean "job done — should be fully collected". */
const COMPLETED_ORDER_STATUSES = new Set<OrderStatus>(['DELIVERED', 'COMPLETED']);

const ts = (value: unknown): number => {
  if (!value || typeof value !== 'string') return 0;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : 0;
};

/** Money still owed on a quote: the effective total (amended order contract
 *  value when a variation moved it, else the frozen grand total) minus
 *  creditable payments (CC surcharge and quarantined entries excluded).
 *  Whole cents, the stored-status rule (`outstandingCents`, backlog #139). */
export const outstandingForQuote = (quote: QuoteListLike): number => {
  const total = effectiveQuoteTotal(quote);
  const paid = sumCreditablePayments(creditableLedgerEntries(quote.paymentLedger));
  return outstandingCents(total, paid) / 100;
};

export interface CompletedBalance {
  order: Order;
  quote: QuoteListLike | null;
  contract: number;
  paid: number;
  outstanding: number;
}

export interface DashboardData {
  /** DRAFT / SENT / ON_HOLD, deduped to the latest version, oldest-touched first. */
  ongoingQuotes: QuoteListLike[];
  /** Confirmed jobs in progress, most-recently-updated first. */
  activeOrders: Order[];
  /** Completed orders whose active quote still has a balance owed, largest first. */
  completedWithBalance: CompletedBalance[];
  totalOutstanding: number;
}

export const buildDashboard = (quotes: QuoteListLike[], orders: Order[]): DashboardData => {
  // Ongoing quotes — collapse each version chain to its latest version so a
  // re-issued quote shows once, not once per version.
  const latestByRoot = new Map<string, QuoteListLike>();
  for (const q of quotes) {
    if (!ONGOING_QUOTE_STATUSES.has(q.status)) continue;
    const root = q.metadata?.rootQuoteId ?? q.id;
    const existing = latestByRoot.get(root);
    if (!existing || (q.metadata?.version ?? 1) >= (existing.metadata?.version ?? 1)) {
      latestByRoot.set(root, q);
    }
  }
  const ongoingQuotes = [...latestByRoot.values()].sort(
    (a, b) =>
      ts(a.metadata?.updatedAt ?? a.metadata?.createdAt) -
      ts(b.metadata?.updatedAt ?? b.metadata?.createdAt),
  );

  const activeOrders = orders
    .filter((p) => ACTIVE_ORDER_STATUSES.has(p.status))
    .sort((a, b) => ts(b.updatedAt ?? b.createdAt) - ts(a.updatedAt ?? a.createdAt));

  // Completed orders still carrying a balance on their active quote.
  const quoteById = new Map(quotes.map((q) => [q.id, q]));
  const completedWithBalance: CompletedBalance[] = [];
  for (const p of orders) {
    if (!COMPLETED_ORDER_STATUSES.has(p.status)) continue;
    const activeQuoteId = p.documentRefs?.activeQuoteId;
    const quote = activeQuoteId ? (quoteById.get(activeQuoteId) ?? null) : null;
    const outstanding = quote ? outstandingForQuote(quote) : 0;
    if (outstanding <= 0) continue;
    completedWithBalance.push({
      order: p,
      quote,
      contract: quote ? effectiveQuoteTotal(quote) : (p.contractValue ?? 0),
      paid: quote ? sumCreditablePayments(creditableLedgerEntries(quote.paymentLedger)) : 0,
      outstanding,
    });
  }
  completedWithBalance.sort((a, b) => b.outstanding - a.outstanding);

  const totalOutstanding = completedWithBalance.reduce((sum, c) => sum + c.outstanding, 0);

  return { ongoingQuotes, activeOrders, completedWithBalance, totalOutstanding };
};
